import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Link,
  Paper,
} from '@mui/material';
import {
  FileText,
  Plus,
  Trash2,
  ExternalLink,
  Cloud,
  HardDrive,
  Link2,
  File,
  Image,
  FileSpreadsheet,
  Presentation,
  FileCode,
  FileArchive,
  Video,
  Music,
} from 'lucide-react';
import dayjs from 'dayjs';
import { Task, LinkedDocument, DocumentProvider } from '@/types';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface TaskDocumentsTabProps {
  task: Task;
  onAddDocument?: (document: Omit<LinkedDocument, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  onRemoveDocument?: (documentId: string) => Promise<void>;
  isLoading?: boolean;
  canEdit?: boolean;
}

interface AddDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (document: Omit<LinkedDocument, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
}

// Get icon based on mime type or file extension
const getDocumentIcon = (mimeType?: string, name?: string) => {
  const extension = name?.split('.').pop()?.toLowerCase();
  
  if (mimeType?.includes('image') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(extension || '')) {
    return <Image size={20} style={{ color: '#10b981' }} />;
  }
  if (mimeType?.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(extension || '')) {
    return <FileSpreadsheet size={20} style={{ color: '#22c55e' }} />;
  }
  if (mimeType?.includes('presentation') || ['pptx', 'ppt'].includes(extension || '')) {
    return <Presentation size={20} style={{ color: '#f97316' }} />;
  }
  if (mimeType?.includes('video') || ['mp4', 'avi', 'mov', 'mkv'].includes(extension || '')) {
    return <Video size={20} style={{ color: '#8b5cf6' }} />;
  }
  if (mimeType?.includes('audio') || ['mp3', 'wav', 'flac'].includes(extension || '')) {
    return <Music size={20} style={{ color: '#ec4899' }} />;
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension || '')) {
    return <FileArchive size={20} style={{ color: '#78716c' }} />;
  }
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'].includes(extension || '')) {
    return <FileCode size={20} style={{ color: '#3b82f6' }} />;
  }
  if (mimeType?.includes('pdf') || extension === 'pdf') {
    return <FileText size={20} style={{ color: '#ef4444' }} />;
  }
  if (mimeType?.includes('document') || ['doc', 'docx', 'txt', 'md'].includes(extension || '')) {
    return <FileText size={20} style={{ color: '#2563eb' }} />;
  }
  return <File size={20} style={{ color: '#64748b' }} />;
};

// Get provider icon and color
const getProviderInfo = (provider: DocumentProvider) => {
  switch (provider) {
    case 'google_drive':
      return { 
        icon: <Cloud size={14} />, 
        label: 'Google Drive', 
        color: '#4285f4',
        bgColor: '#e8f0fe'
      };
    case 'onedrive':
      return { 
        icon: <Cloud size={14} />, 
        label: 'OneDrive', 
        color: '#0078d4',
        bgColor: '#e6f2ff'
      };
    case 'sharepoint':
      return { 
        icon: <HardDrive size={14} />, 
        label: 'SharePoint', 
        color: '#036c70',
        bgColor: '#e6f5f5'
      };
    case 'manual':
    default:
      return { 
        icon: <Link2 size={14} />, 
        label: 'Manual Link', 
        color: '#64748b',
        bgColor: '#f1f5f9'
      };
  }
};

// Add Document Modal
const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ open, onClose, onAdd }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    provider: 'manual' as DocumentProvider,
    mimeType: '',
  });

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.url.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onAdd({
        name: formData.name.trim(),
        url: formData.url.trim(),
        provider: formData.provider,
        mimeType: formData.mimeType || undefined,
      });
      setFormData({ name: '', url: '', provider: 'manual', mimeType: '' });
      onClose();
    } catch (error) {
      console.error('Failed to add document:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Document</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Document Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Requirements Specification"
            fullWidth
            required
          />
          <TextField
            label="URL"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="https://drive.google.com/..."
            fullWidth
            required
          />
          <AppSelect
            label="Provider"
            value={formData.provider}
            onChange={(e) => setFormData({ ...formData, provider: e.target.value as DocumentProvider })}
          >
            <AppMenuItem value="manual">Manual Link</AppMenuItem>
            <AppMenuItem value="google_drive">Google Drive</AppMenuItem>
            <AppMenuItem value="onedrive">OneDrive</AppMenuItem>
            <AppMenuItem value="sharepoint">SharePoint</AppMenuItem>
          </AppSelect>
          <TextField
            label="MIME Type (optional)"
            value={formData.mimeType}
            onChange={(e) => setFormData({ ...formData, mimeType: e.target.value })}
            placeholder="e.g., application/pdf"
            fullWidth
            size="small"
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> You can also integrate with Google Drive or OneDrive for automatic file picking.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.name.trim() || !formData.url.trim() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Plus size={16} />}
        >
          Link Document
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * TaskDocumentsTab
 * Manage linked documents from various providers (Google Drive, OneDrive, manual links)
 */
const TaskDocumentsTab: React.FC<TaskDocumentsTabProps> = ({
  task,
  onAddDocument,
  onRemoveDocument,
  isLoading,
  canEdit,
}) => {
  const editable = canEdit ?? true;
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const documents = task.linkedDocuments || [];

  const handleRemoveDocument = async (documentId: string) => {
    if (!onRemoveDocument || !editable) return;
    setRemovingId(documentId);
    try {
      await onRemoveDocument(documentId);
    } finally {
      setRemovingId(null);
    }
  };

  // Group documents by provider
  const groupedDocuments = documents.reduce((acc, doc) => {
    const provider = doc.provider || 'manual';
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(doc);
    return acc;
  }, {} as Record<DocumentProvider, LinkedDocument[]>);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {documents.length} document{documents.length !== 1 ? 's' : ''} linked
        </Typography>
        {editable && onAddDocument && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setShowAddModal(true)}
          >
            Link Document
          </Button>
        )}
      </Box>

      {/* Integration Status */}
      {task.integrations?.drive?.enabled && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Connected to {task.integrations.drive.provider === 'google_drive' ? 'Google Drive' : 'OneDrive'}
            {task.integrations.drive.folderName && ` - ${task.integrations.drive.folderName}`}
          </Typography>
        </Alert>
      )}

      {/* Empty State */}
      {documents.length === 0 ? (
        <Paper
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <FileText size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Documents Linked
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Link documents from Google Drive, OneDrive, or add manual links
          </Typography>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => setShowAddModal(true)}
          >
            Link Your First Document
          </Button>
        </Paper>
      ) : (
        <List disablePadding>
          {documents.map((doc) => {
            const providerInfo = getProviderInfo(doc.provider);
            const isRemoving = removingId === doc.id;

            return (
              <ListItem
                key={doc.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getDocumentIcon(doc.mimeType, doc.name)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Link
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: 'none',
                          color: 'text.primary',
                          fontWeight: 500,
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                        }}
                      >
                        {doc.name}
                      </Link>
                      <Chip
                        icon={providerInfo.icon}
                        label={providerInfo.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: providerInfo.bgColor,
                          color: providerInfo.color,
                          '& .MuiChip-icon': { color: providerInfo.color },
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Linked {dayjs(doc.linkedAt).fromNow()}
                      {doc.size && ` • ${(doc.size / 1024 / 1024).toFixed(2)} MB`}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Open in new tab">
                    <IconButton
                      size="small"
                      onClick={() => window.open(doc.url, '_blank')}
                      sx={{ mr: 0.5 }}
                    >
                      <ExternalLink size={16} />
                    </IconButton>
                  </Tooltip>
                  {editable && onRemoveDocument && (
                    <Tooltip title="Remove link">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveDocument(doc.id)}
                        disabled={isRemoving}
                      >
                        {isRemoving ? <CircularProgress size={16} /> : <Trash2 size={16} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Add Document Modal */}
      {editable && onAddDocument && (
        <AddDocumentModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={onAddDocument}
        />
      )}
    </Box>
  );
};

export default TaskDocumentsTab;
